/**
 * The ordered list of shell modules (#92). The rail and the dashboard
 * both render from this list, so adding a module is one entry here plus
 * its locale strings.
 */

import type CompareService from '../../core/compare-service';
import { createDashboardModule } from '../modules/dashboard/dashboard-module';
import { createReaderModule } from '../modules/reader/reader-module';
import { createStatusModule } from '../modules/status/status-module';
import { createAccountModule } from '../modules/account/account-module';
import { createIntegrationsModule } from '../modules/integrations/integrations-module';
import { createFilesModule } from '../modules/files/files-module';
import { createTodoModule } from '../modules/todo/todo-module';
import type { ProcessedDocument } from '../../core/processed-documents';
import type FilesService from '../../core/files-service';
import type IntegrationsService from '../../core/integrations-service';
import type {
  NextcloudStatus,
  StoredAnalysis,
} from '../../core/integrations-service';
import type { AppModule } from './module.types';
import type { TranslationParams } from '../../i18n/ui-manager.types';
import type { ConfirmDialogOptions } from '../../components/confirm-dialog';
import type { NameDialogOptions } from '../../components/name-dialog';
import type { MoveDialogOptions } from '../../components/move-dialog';
import type AccountSettings from '../../components/account-settings';

export interface ModuleRegistryDeps {
  t: (key: string, params?: TranslationParams) => string;
  onLanguageChange: (callback: () => void) => void;
  getUserName: () => string | null;
  getLastOpenedName: () => string | null;
  topbarSlot: HTMLElement;
  closeReaderOverlays: () => void;
  getAccountView: () => AccountSettings | null;
  navigate: (path: string) => void;
  /** #92 follow-up chrome cleanup: null in direct/dev mode (no authService). */
  logout: (() => void) | null;
  /** #93: null until proxy-mode auth exists. */
  getFilesService: () => FilesService | null;
  /** #140: `source` is the collection document the file was downloaded from. */
  openInReader: (
    file: File,
    source?: { id: string; name: string }
  ) => Promise<void>;
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
  nameDialog: (options: NameDialogOptions) => Promise<boolean>;
  moveDialog: (
    options: MoveDialogOptions
  ) => Promise<string | null | undefined>;
  filesEnabled: boolean;
  /** #98: null outside proxy mode (the Nextcloud card stays a placeholder). */
  getIntegrationsService?: () => IntegrationsService | null;
  /** #140: null outside proxy mode; modules receive it in later tasks. */
  getCompareService?: () => CompareService | null;
  /** #140, flag on: the Reader's Compare panel; returns its cleanup. */
  mountComparePanel?: (container: HTMLElement) => () => void;
  /** #140, flag on: the collection document open in the Reader, if any. */
  getReaderDocumentId?: () => string | null;
  /** #140, flag on: the Reader was left (cancels an Add document in flight). */
  onReaderLeave?: () => void;
  /** #98 part 4: show a stored analysis in the Reader. */
  openStoredAnalysis?: (
    stored: StoredAnalysis,
    source: { name: string; folderId: string; path: string }
  ) => Promise<void>;
  nextcloudConnectDialog?: (opts: {
    service: IntegrationsService;
  }) => Promise<NextcloudStatus | null>;
  nextcloudFolderPicker?: (opts: {
    service: IntegrationsService;
  }) => Promise<NextcloudStatus | null>;
  /** #120: VITE_FEATURE_INTEGRATIONS. */
  integrationsEnabled: boolean;
  /** #96: VITE_FEATURE_TODO. */
  todoEnabled: boolean;
  /** #140: VITE_FEATURE_COMPARE. */
  compareEnabled: boolean;
  listProcessed: () => ProcessedDocument[];
  openProcessed: (document: ProcessedDocument) => void;
}

export function createModules(deps: ModuleRegistryDeps): AppModule[] {
  const modules: AppModule[] = [];
  modules.push(
    createDashboardModule({
      getModules: () => modules,
      t: deps.t,
      getUserName: deps.getUserName,
      onLanguageChange: deps.onLanguageChange,
    }),
    createReaderModule({
      getLastOpenedName: deps.getLastOpenedName,
      t: deps.t,
      topbarSlot: deps.topbarSlot,
      closeOverlays: deps.closeReaderOverlays,
      compareEnabled: deps.compareEnabled,
      mountComparePanel: deps.mountComparePanel,
      onLeave: deps.onReaderLeave,
    }),
    createFilesModule({
      t: deps.t,
      onLanguageChange: deps.onLanguageChange,
      getService: deps.getFilesService,
      openInReader: deps.openInReader,
      confirm: deps.confirm,
      nameDialog: deps.nameDialog,
      moveDialog: deps.moveDialog,
      navigate: deps.navigate,
      enabled: deps.filesEnabled,
      compareEnabled: deps.compareEnabled,
      // #98 part 3: the Nextcloud folder only with the integrations switch on.
      getIntegrationsService: deps.integrationsEnabled
        ? deps.getIntegrationsService
        : undefined,
      openStoredAnalysis: deps.openStoredAnalysis,
    }),
    createTodoModule({
      t: deps.t,
      onLanguageChange: deps.onLanguageChange,
      // A finished comparison navigates to #/todo, so Compare needs the Todo module (#141).
      enabled: Boolean(deps.todoEnabled || deps.compareEnabled),
      compareEnabled: deps.compareEnabled,
      getCompareService: deps.getCompareService,
      getFilesService: deps.getFilesService,
      openInReader: deps.openInReader,
      getReaderDocumentId: deps.getReaderDocumentId,
      navigate: deps.navigate,
      listProcessed: deps.listProcessed,
      openDocument: deps.openProcessed,
    }),
    createIntegrationsModule({
      t: deps.t,
      onLanguageChange: deps.onLanguageChange,
      enabled: deps.integrationsEnabled,
      getService: deps.getIntegrationsService,
      connectDialog: deps.nextcloudConnectDialog,
      folderPicker: deps.nextcloudFolderPicker,
      confirm: deps.confirm,
    }),
    createStatusModule({
      t: deps.t,
      onLanguageChange: deps.onLanguageChange,
    }),
    createAccountModule({
      t: deps.t,
      getUserName: deps.getUserName,
      getView: deps.getAccountView,
      navigate: deps.navigate,
      logout: deps.logout,
      onLanguageChange: deps.onLanguageChange,
    })
  );
  return modules;
}
